import { useTranslations } from '../../i18n';
import { theme } from '../../lib/theme';
import { FileText } from 'lucide-react';
import { isPdfFile } from '../../lib/pdfUtils';
import { CachedImage } from '../ui/CachedImage';
import { SupportedLanguage } from '../../lib/expenseTypeHelpers';

interface GalleryItemProps {
    item: {
        id: string;
        imageUrl: string;
        thumbnail?: string;
        title: string;
        subtitle?: string;
        date?: string;
        onClick?: () => void;
    };
    compact?: boolean;
}

export function GalleryItem({ item, compact = true }: GalleryItemProps) {
    const { language } = useTranslations();

    return (
        <button
            type="button"
            onClick={item.onClick}
            className={`w-full group text-left flex items-start gap-4 ${compact ? 'p-4' : theme.spacing.cardPadding} transition-all hover:bg-gray-50 dark:hover:bg-white/5 active:scale-[0.99]`}
        >
            <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-gray-100 dark:bg-white/5 border border-gray-100 dark:border-white/10`}
            >
                {(item.thumbnail || item.imageUrl) && !isPdfFile(item.imageUrl) ? (
                    <CachedImage
                        src={item.thumbnail || item.imageUrl}
                        alt={item.title}
                        className="w-full h-full object-cover"
                    />
                ) : item.thumbnail ? (
                    <CachedImage
                        src={item.thumbnail}
                        alt={item.title}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <FileText size={20} className="text-red-500 dark:text-red-400" />
                )}
            </div>

            <div className="flex-1 min-w-0 flex items-start gap-3">
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <h4 className={`${theme.typography.itemTitle} truncate`}>
                        {item.title}
                    </h4>
                    {item.subtitle && (
                        <p className="text-[13px] text-gray-500 dark:text-gray-400 font-medium leading-relaxed line-clamp-2 italic mt-0.5">
                            {item.subtitle}
                        </p>
                    )}
                </div>

                <div className="flex flex-col items-end gap-0.5 flex-shrink-0 pt-0.5">
                    <div className="flex items-center gap-2">
                        {item.date && (
                            <span className={`${theme.typography.itemSubtitle} whitespace-nowrap`}>
                                {new Date(item.date).toLocaleDateString(language as SupportedLanguage, { day: '2-digit', month: 'short' })}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </button>
    );
}
